import { Stack } from "@fluentui/react";
import React from "react";
import { getExchangeMessageHTMLBody } from "../../../src/taskpane/services/ExchangeService";
import Loader from "../../../src/taskpane/components/Loader";
import { useResizeHandle } from "../../../src/taskpane/components/hooks/useResizeHandle";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { displayHeight } from "../../../src/taskpane/config/constants";

const deviderItemStyles: React.CSSProperties = {
    background: "var(--db-border, #f1f1f1)",
    height: displayHeight,
    width: 5,
    cursor: "col-resize",
    // Without this a touch drag pans the pane instead of raising pointermove.
    touchAction: "none",
};

const htmlContentContainerStyles: React.CSSProperties = {
    height: "100%",
    width: "100%",
    position: "relative"
}

const htmlFrameStyles: React.CSSProperties = {
    height: "100%",
    width: "100%",
    border: "none",
    position: "relative",
    userSelect: "none",
    zIndex: 0
}

type ExchangePreviewProps = {
    mailboxId: string;
    messageId: string;
    initialWidth: number;
    minWidth: number;
};

export const ExchangePreview = ({ mailboxId, messageId, initialWidth, minWidth }: ExchangePreviewProps) => {
    const languageStrings = React.useContext(LanguageContext);
    const [isLoading, setIsLoading] = React.useState(false);
    const [htmlContent, setHTMLContent] = React.useState<string>();
    const [width, setWidth] = React.useState(initialWidth);

    const resizeHandleProps = useResizeHandle(
        (clientX: number) => {
            const offsetRight = document.body.offsetWidth - (clientX - document.body.offsetLeft);
            if (offsetRight >= minWidth) {
                setWidth(offsetRight);
            }
        }
    );

    React.useEffect(() => {
        (async () => {
            setIsLoading(true);
            setHTMLContent(await getExchangeMessageHTMLBody(mailboxId, messageId));
            setIsLoading(false);
        })();
    }, [messageId, setIsLoading]);

    return (
        <Stack horizontal styles={{ root: { height: displayHeight, width: width } }}>
            <Stack.Item>
                <div style={deviderItemStyles} {...resizeHandleProps} />
            </Stack.Item>
            <Stack.Item grow>
                {isLoading
                    ? <Loader label={languageStrings.Loading} hideLabel={true} />
                    : (htmlContent && <div style={htmlContentContainerStyles}>
                        {/*
                          There used to be a 75px transparent overlay pinned to the left
                          edge here. It existed because the old splitter tracked the drag
                          on `document`, which stops firing the moment the pointer crosses
                          into this iframe — the overlay bought back the first 75px. The
                          splitter uses pointer capture now, so the drag survives the whole
                          iframe and the overlay only cost the user the left 75px of every
                          email body.
                        */}
                        <iframe srcDoc={htmlContent} sandbox="" style={htmlFrameStyles} />
                    </div>)}
            </Stack.Item>
        </Stack>
    );
};